import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, Check, Inbox } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { API } from "@/lib/api";

const fmtDate = (d) => new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
    setLeads(data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(load)
      .catch(() => navigate("/admin/login"));
  }, [load, navigate]);

  const markRead = async (id) => {
    await axios.patch(`${API}/leads/${id}/read`, {}, { withCredentials: true });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, letto: true } : l)));
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const nuove = leads ? leads.filter((l) => !l.letto).length : 0;

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="admin-dashboard">
      <header className="border-b border-line">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6">
          <div className="flex items-center gap-3 text-ink">
            <Logo markClass="h-8 w-auto" />
            <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A8D8B]">Richieste contatto</span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8A8D8B] transition-colors hover:text-terracotta"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.25} /> Esci
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="flex gap-10 border-b border-line pb-6" data-testid="leads-stats">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A8D8B]">Totale richieste</p>
            <p className="mt-1 font-serif text-4xl" data-testid="leads-total">{leads ? leads.length : "—"}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A8D8B]">Da leggere</p>
            <p className="mt-1 font-serif text-4xl text-terracotta" data-testid="leads-unread">{leads ? nuove : "—"}</p>
          </div>
        </div>

        {leads === null ? (
          <p className="py-20 text-center text-sm text-[#8A8D8B]" data-testid="leads-loading">Caricamento…</p>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24 text-[#8A8D8B]" data-testid="leads-empty">
            <Inbox className="h-10 w-10" strokeWidth={1} />
            <p className="text-sm">Nessuna richiesta ricevuta finora.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="mt-6 w-full min-w-[900px] border-collapse text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8D8B]">
                  <th className="pb-3 pr-4">Stato</th>
                  <th className="pb-3 pr-4">Data</th>
                  <th className="pb-3 pr-4">Nome</th>
                  <th className="pb-3 pr-4">Contatti</th>
                  <th className="pb-3 pr-4">Tipologia</th>
                  <th className="pb-3 pr-4">Messaggio</th>
                  <th className="pb-3" />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-line align-top" data-testid={`lead-row-${l.id}`}>
                    <td className="py-4 pr-4">
                      <span
                        className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                          l.letto ? "bg-bone text-[#8A8D8B]" : "bg-terracotta text-ivory"
                        }`}
                        data-testid={`lead-status-${l.id}`}
                      >
                        {l.letto ? "Letta" : "Nuova"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-4 pr-4 text-[#4A4F4C]">{fmtDate(l.created_at)}</td>
                    <td className="py-4 pr-4 font-medium">
                      {l.nome} {l.cognome}
                    </td>
                    <td className="py-4 pr-4 text-[#4A4F4C]">
                      <div>{l.email}</div>
                      <div>{l.telefono}</div>
                    </td>
                    <td className="py-4 pr-4 text-[#4A4F4C]">{l.tipologia || "—"}</td>
                    <td className="max-w-[280px] py-4 pr-4 text-[#4A4F4C]">{l.messaggio || "—"}</td>
                    <td className="py-4">
                      {!l.letto && (
                        <button
                          onClick={() => markRead(l.id)}
                          data-testid={`lead-mark-read-${l.id}`}
                          className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.14em] text-olive transition-colors hover:text-terracotta"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={1.5} /> Segna letta
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
